import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Phone, Mail, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';

const Complaints = () => {
  const process = [
    {
      step: '1',
      title: 'Acknowledgement',
      description: 'Complaints will be acknowledged within 3 working days'
    },
    {
      step: '2',
      title: 'Investigation',
      description: 'We will investigate your concern fairly and thoroughly'
    },
    {
      step: '3',
      title: 'Response',
      description: 'A full response will be provided within 20 working days'
    },
    {
      step: '4',
      title: 'Resolution',
      description: 'We aim to resolve concerns to your satisfaction'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <MessageSquare className="w-16 h-16 text-[#4A90E2] mx-auto" />
            <h1 className="heading-hero">Complaints & Compliments</h1>
            <p className="body-large">
              We welcome feedback, complaints, and compliments as part of our commitment to continuous improvement.
            </p>
          </div>
        </div>
      </section>

      {/* How to Complain */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">How to Make a Complaint</h2>
            <p className="body-medium text-gray-700 text-center mb-8">
              Complaints can be made verbally or in writing by service users, relatives, carers, professionals, or representatives.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <Card className="p-8 border-0 bg-[#E8F4FC] text-center">
                <Mail className="w-12 h-12 text-[#4A90E2] mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-3">Email</h3>
                <a 
                  href="mailto:info@threesphereshealthcare.org"
                  className="text-[#4A90E2] hover:underline break-all"
                >
                  info@threesphereshealthcare.org
                </a>
              </Card>
              
              <Card className="p-8 border-0 bg-[#E8F4FC] text-center">
                <Phone className="w-12 h-12 text-[#4A90E2] mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-3">Phone</h3>
                <a 
                  href="tel:03300433693"
                  className="text-[#4A90E2] hover:underline text-lg"
                >
                  0330 043 3693
                </a>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="heading-2 mb-12 text-center">What You Can Expect</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {process.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto mt-12">
            <Card className="p-8 border-0">
              <h3 className="font-semibold text-lg mb-4 text-gray-900">Our Promise to You</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Complaints will be handled fairly, confidentially, and without prejudice</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">We will keep you informed throughout the process</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">We will learn from complaints to improve our services</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Escalation */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-6 text-center">Escalation</h2>
            <p className="body-medium text-gray-700 text-center mb-8">
              If you are not satisfied with our response, you may escalate your concern to the relevant commissioning authority or regulatory body, depending on the service provided.
            </p>
          </div>
        </div>
      </section>

      {/* Compliments */}
      <section className="section bg-[#E8F4FC]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <CheckCircle className="w-12 h-12 text-[#4A90E2] mx-auto mb-6" />
            <h2 className="heading-2 mb-6">We Also Welcome Compliments</h2>
            <p className="body-medium text-gray-700">
              We also welcome positive feedback and compliments, which help us recognise good practice and improve services. Please share your positive experiences with us.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="heading-2 text-white">Get in Touch</h2>
            <p className="body-large text-white/90">
              Whether you have a complaint or compliment, we want to hear from you.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#4A90E2] hover:bg-gray-100">
                Contact Us
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Complaints;
